import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Empty, Form, Input, Popconfirm, Table, Tag } from 'antd'
import i18next from 'i18next'
import { debounce, get, isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'
import { DeleteOutlined, PlusCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons'

import { ColumnsType, TablePaginationConfig } from 'antd/lib/table'
import { FilterValue, SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface'
import { getFormInitialValues, initialize } from 'redux-form'
import { NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params'
import { deletePriceOffer, PriceOfferTableItem } from '../../reducers/priceOffers/actions'
import { history } from '../../utils/history'
import { RootState } from '../../reducers'
import { FORM, translatePriceOfferStatusColor, translatePriceOfferStatusToSk } from '../../utils/enums'
import queryKeys from '../../queries/queryKeys'
import fetchPriceOffersList from '../../queries/priceOffers/fetchPriceOffers'

const PAGE_SIZE = 200

type Columns = ColumnsType<PriceOfferTableItem>

type Props = {
	statusFilter?: string[]
	deleted?: boolean
	onCountChange?: (count: number) => void
	queryParamPrefix?: string
}

const PriceOffers = ({ statusFilter, deleted = false, onCountChange, queryParamPrefix = '' }: Props) => {
	const dispatch = useDispatch()
	const queryClient = useQueryClient()

	const { t } = useTranslation('pages')

	const filterInitialValues = useSelector((state: RootState) => getFormInitialValues(FORM.PRICE_OFFERS_FILTER)(state))

	const [query, setQuery] = useQueryParams({
		search: StringParam,
		limit: NumberParam,
		page: withDefault(NumberParam, 1),
		orderBy: withDefault(StringParam, 'address'),
		orderDirection: withDefault(StringParam, null)
	})

	const { data: priceOffersData, isLoading: isPriceOffersLoading } = useQuery({
		queryKey: queryKeys.priceOffers.list({
			page: query.page,
			limit: PAGE_SIZE,
			search: query.search,
			orderBy: query.orderBy,
			orderDirection: query.orderDirection,
			status: statusFilter,
			deleted
		}),
		queryFn: () => fetchPriceOffersList(
			query.page,
			PAGE_SIZE,
			{
				search: query.search,
				orderBy: query.orderBy,
				orderDirection: query.orderDirection,
				...(statusFilter?.length ? { status: statusFilter } : {})
			},
			deleted
		),
		keepPreviousData: true
	})

	const dataSource = priceOffersData || { tableData: [], pagination: null }

	useEffect(() => {
		if (onCountChange && priceOffersData?.pagination?.totalCount != null) {
			onCountChange(priceOffersData.pagination.totalCount)
		}
	}, [priceOffersData?.pagination?.totalCount])

	useEffect(() => {
		if (!filterInitialValues || isEmpty(filterInitialValues)) {
			const initData = {
				search: query.search || null
			}
			dispatch(initialize(FORM.PRICE_OFFERS_FILTER, initData))
		}
	}, [dispatch, filterInitialValues, query.search])

	const columns: Columns = [
		{
			title: i18next.t('pages:Adresa'),
			dataIndex: 'address',
			key: 'address',
			ellipsis: true,
			sorter: true,
			render: (value) => value || '-'
		},
		...(deleted ? [] : [{
			title: i18next.t('pages:Status'),
			dataIndex: 'status',
			key: 'status',
			fixed: 'right' as const,
			width: 150,
			render(status: string) {
				return (
					<span>
						<Tag color={translatePriceOfferStatusColor(status as any)}>
							{translatePriceOfferStatusToSk(status as any)}
						</Tag>
					</span>
				)
			}
		}]),
		...(deleted ? [
			{
				title: i18next.t('pages:Vymazal'),
				dataIndex: 'deletedBy',
				key: 'deletedBy',
				fixed: 'right' as const,
				width: 160,
				render: (value: { id: number; name: string } | null) => value?.name || '-'
			},
			{
				title: i18next.t('pages:Dátum vymazania'),
				dataIndex: 'deletedAt',
				key: 'deletedAt',
				fixed: 'right' as const,
				width: 180,
				render: (value: string) => value || '-'
			}
		] : []),
		...(!deleted ? [{
			key: 'operation',
			fixed: 'right' as const,
			width: 50,
			render(_: any, record: PriceOfferTableItem) {
				return (
					<Popconfirm
						title={ i18next.t('pages:Skutočne chcete vymazať záznam?') }
						icon={ <QuestionCircleOutlined style={ { color: 'red' } } /> }
						cancelText={ i18next.t('pages:Zrušiť') }
						okText={ i18next.t('pages:Vymazať') }
						onConfirm={ (e) => {
							e?.stopPropagation()
							dispatch(deletePriceOffer(record.id, () => {
								setQuery({
									limit: PAGE_SIZE,
									...query,
									page: 1
								})
								queryClient.invalidateQueries({ queryKey: ['priceOffers', 'list'] })
							}))
						} }
						onCancel={ (e) => e?.stopPropagation() }
						okButtonProps={ { size: 'small', type: 'primary' } }
						cancelButtonProps={ { size: 'small', type: 'ghost' } }
					>
						<Button
							icon={ <DeleteOutlined /> }
							type={ 'primary' }
							onClick={ (e) => e.stopPropagation() }
						/>
					</Popconfirm>
				)
			}
		}] : [])
	]

	const handleTableChange = (
		pagination: TablePaginationConfig,
		filters: Record<string, FilterValue | null>,
		sorter: SorterResult<PriceOfferTableItem> | SorterResult<PriceOfferTableItem>[],
		extra: TableCurrentDataSource<PriceOfferTableItem>
	) => {
		let order = {}
		if ((sorter as SorterResult<any>)?.order) {
			const sorterLocal = (sorter as SorterResult<any>)
			order = {
				orderBy: sorterLocal.field,
				orderDirection: sorterLocal.order === 'ascend' ? 'asc' : 'desc'
			}
		}

		setQuery({
			limit: PAGE_SIZE,
			...query,
			page: pagination.current,
			...order
		})
	}

	const debounced = useCallback(debounce((searchTerm: string) => setQuery({ ...query, search: searchTerm }), 300), [query])

	const handleOnChange = ((e: React.ChangeEvent<HTMLInputElement>) => {
		debounced(e.target.value)
	})

	return (
		<div className={ 'page-wrapper' }>
			<div className={ 'flex justify-between' }>
				<Form.Item>
					<Input.Search onChange={ handleOnChange } style={ { width: 300 } } allowClear />
				</Form.Item>
				{ !deleted && (
					<Button
						icon={ <PlusCircleOutlined /> }
						href={ t('paths:priceOfferCreate|path') }
						type={ 'primary' }
					>
						{ t('pages:Vytvoriť cenovú ponuku') }
					</Button>
				) }
			</div>
			<Table
				className={ 'general-table' }
				columns={ columns }
				dataSource={ dataSource.tableData }
				onChange={ handleTableChange }
				style={ { marginTop: -20 } }
				showSorterTooltip={ false }
				pagination={ {
					pageSize: PAGE_SIZE,
					total: dataSource.pagination?.totalCount,
					current: dataSource.pagination?.page,
					showSizeChanger: false
				} }
				loading={ isPriceOffersLoading }
				onRow={ (record) => ({
					onClick: () => !deleted && history.push(`${t('paths:priceOfferDetail|path')}/${get(record, 'id')}`)
				}) }
				locale={ {
					emptyText: (
						<Empty description={ t('pages:Žiadne dáta') } />
					)
				} }
				size={ 'small' }
			/>
		</div>
	)
}

export default PriceOffers
